import { useState, useEffect } from "react";
import { MapPin, Flame, ExternalLink } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { ImageWithFallback } from "../figma/ImageWithFallback";
import { Button } from "../ui/button";
import { apiFetch } from "../../services/api/Dashboard";
import PageHeader from "../Header";

interface Venue {
  id: number;
  title: string;
  location: string;
  description: string;
  link: string;
  sports: string[];
  image_url: string;
  is_active: boolean;
}

interface PaginationMeta {
  current_page: number;
  last_page: number;
  links: { url: string | null; label: string; active: boolean }[];
}

export function Venues() {
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [page, setPage] = useState(1);
  const [selectedVenue, setSelectedVenue] = useState<Venue | null>(null);

  const fetchVenues = async (pageNum = 1) => {
    setLoading(true);
    try {
      const res = await apiFetch(`view-all-venues?page=${pageNum}`);

      if (Array.isArray(res?.data?.data)) {
        setVenues(res.data.data);
        setMeta(res.data.meta || null);
      } else if (Array.isArray(res?.data)) {
        setVenues(res.data);
        setMeta(null);
      } else {
        console.warn("Unexpected venues format:", res);
        setVenues([]);
        setMeta(null);
      }
    } catch (error) {
      console.error("Error fetching venues:", error);
      setVenues([]);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVenues(page);
  }, [page]);

  const handlePageChange = (newPage: number) => {
    if (meta && newPage >= 1 && newPage <= meta.last_page) {
      setPage(newPage);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-20 text-gray-500 text-lg">
        Loading venues...
      </div>
    );
  }

  if (venues.length === 0) {
    return (
      <div className="text-center py-20 text-gray-500 text-lg">
        No venues available.
      </div>
    );
  }

  return (
    <section>
      <PageHeader
        title="Venues & Facilities"
        paragraph="Explore India's top sporting complexes and world-class training facilities"
      />
      <div className="max-w-7xl mx-auto m-5">
        {/* Responsive Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 justify-items-center">
          {venues.map((venue) => (
            <Card
              key={venue.id}
              className="
      w-full
      max-w-[360px]
      overflow-hidden
      rounded-md
      bg-white
      group
      relative
      transition-all
      duration-300
      hover:-translate-y-1
      hover:shadow-lg
    "
            >
              {/* Image Section */}
              <div className="relative overflow-hidden">
                <ImageWithFallback
                  src={venue.image_url}
                  alt={venue.title}
                  className="
          w-full
          h-48
          sm:h-52
          object-cover
          transform
          transition-transform
          duration-500
          group-hover:scale-105
        "
                />

                {/* Dark Gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

                {/* Google Maps */}
                {venue.link && (
                  <a
                    href={venue.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Open ${venue.title} in Google Maps`}
                    onClick={(e) => e.stopPropagation()}
                    className="
            absolute
            top-3
            right-3
            z-30
            flex
            h-9
            w-9
            items-center
            justify-center
            rounded-full
            bg-white/90
            text-gray-700
            shadow-md
            transition-all
            hover:bg-white
            hover:scale-105
          "
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                )}

                {/* Title */}
                <div className="absolute bottom-0 left-0 right-0 z-20 p-4">
                  <h3 className="pr-8 text-base sm:text-lg font-semibold leading-snug text-white drop-shadow-md">
                    {venue.title}
                  </h3>
                </div>
              </div>

              {/* Card Content */}
              <CardContent className="p-4">
                {/* Location */}
                <div className="flex items-center gap-2 text-sm sm:text-base">
                  <MapPin className="h-5 w-5 shrink-0 text-orange-600" />

                  <span className="truncate font-medium text-gray-700">
                    {venue.location}
                  </span>
                </div>

                {/* View More */}
                <Button
                  type="button"
                  onClick={() => setSelectedVenue(venue)}
                  className="
          mt-4
          w-full
          rounded-lg
          bg-orange-600
          py-2.5
          text-sm
          font-semibold
          text-white
          transition
          hover:bg-orange-700
        "
                >
                  View More
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Pagination Controls */}
        {meta && meta.last_page > 1 && (
          <div className="flex justify-center items-center mt-10 space-x-2">
            <Button
              variant="outline"
              className="px-4 py-2 text-sm"
              disabled={page === 1}
              onClick={() => handlePageChange(page - 1)}
            >
              Previous
            </Button>

            {meta.links
              .filter(
                (link) =>
                  !link.label.includes("&laquo;") &&
                  !link.label.includes("&raquo;"),
              )
              .map((link, idx) => (
                <Button
                  key={idx}
                  variant={link.active ? "default" : "outline"}
                  className={`px-4 py-2 text-sm ${
                    link.active ? "bg-orange-700 text-white" : "text-orange-700"
                  }`}
                  onClick={() => {
                    const url = link.url;
                    const newPage = url
                      ? parseInt(url.split("page=")[1] || "1")
                      : 1;
                    handlePageChange(newPage);
                  }}
                >
                  {link.label}
                </Button>
              ))}

            <Button
              variant="outline"
              className="px-4 py-2 text-sm"
              disabled={page === meta.last_page}
              onClick={() => handlePageChange(page + 1)}
            >
              Next
            </Button>
          </div>
        )}

        {/* Stylish Responsive Pagination */}
        {meta && meta.last_page > 1 && (
          <div className="flex flex-wrap justify-center items-center mt-10 gap-2 sm:gap-3">
            {/* Prev button */}
            <Button
              variant="outline"
              className="px-4 py-2 text-sm rounded-full border-oragne-600 text-orange-700 hover:bg-orange-600 hover:text-white transition"
              disabled={page === 1}
              onClick={() => handlePageChange(page - 1)}
            >
              ← Prev
            </Button>

            {/* Dynamic page buttons with dots */}
            {(() => {
              const pages = Array.from(
                { length: meta.last_page },
                (_, i) => i + 1,
              )
                .filter((num) => {
                  return (
                    num === 1 ||
                    num === meta.last_page ||
                    (num >= page - 1 && num <= page + 1)
                  );
                })
                .reduce<(number | string)[]>((acc, curr, idx, arr) => {
                  if (idx > 0 && arr[idx - 1] !== curr - 1) acc.push("...");
                  acc.push(curr);
                  return acc;
                }, []);

              return pages.map((num, idx) =>
                num === "..." ? (
                  <span
                    key={idx}
                    className="text-gray-600 font-semibold px-2 select-none"
                  >
                    ...
                  </span>
                ) : (
                  <Button
                    key={idx}
                    variant={num === page ? "default" : "outline"}
                    className={`px-4 py-2 text-sm rounded-full transition ${
                      num === page
                        ? "bg-orange-600 text-white border-orange-600 shadow-md"
                        : "border-orange-600 text-orange-700 hover:bg-orange-600 hover:text-white"
                    }`}
                    onClick={() => {
                      if (typeof num === "number") handlePageChange(num);
                    }}
                  >
                    {num}
                  </Button>
                ),
              );
            })()}

            {/* Next button */}
            <Button
              variant="outline"
              className="px-4 py-2 text-sm rounded-full border-oragne-600 text-orange-700 hover:bg-orange-600 hover:text-white transition"
              disabled={page === meta.last_page}
              onClick={() => handlePageChange(page + 1)}
            >
              Next →
            </Button>
          </div>
        )}
        {/* Venue Details Modal */}
        {selectedVenue && (
          <div
            className="
      fixed
      inset-0
      z-[9999]
      flex
      items-end
      sm:items-center
      justify-center
      bg-black/60
      p-0
      sm:p-4
    "
            onClick={() => setSelectedVenue(null)}
          >
            <div
              className="
        relative
        flex
        w-full
        sm:w-[95%]
        lg:w-full
        max-w-3xl
        max-h-[95vh]
        sm:max-h-[90vh]
        flex-col
        overflow-hidden
        rounded-t-2xl
        sm:rounded-2xl
        bg-white
        shadow-2xl
      "
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                type="button"
                onClick={() => setSelectedVenue(null)}
                aria-label="Close"
                className="
          absolute
          right-3
          top-3
          z-50
          flex
          h-9
          w-9
          items-center
          justify-center
          rounded-full
          bg-black/60
          text-xl
          text-white
          transition
          hover:bg-black/80
        "
              >
                ×
              </button>

              {/* Modal Image */}
              <div className="relative h-52 shrink-0 sm:h-64 md:h-72">
                <ImageWithFallback
                  src={selectedVenue.image_url}
                  alt={selectedVenue.title}
                  className="h-full w-full object-cover"
                />

                {/* Gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

                {/* Title */}
                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
                  <h2 className="pr-10 text-xl font-bold leading-tight text-white sm:text-2xl md:text-3xl">
                    {selectedVenue.title}
                  </h2>

                  <div className="mt-2 flex items-center gap-2 text-sm text-gray-200 sm:text-base">
                    <MapPin className="h-4 w-4 shrink-0 sm:h-5 sm:w-5" />

                    <span>{selectedVenue.location}</span>
                  </div>
                </div>
              </div>

              {/* Scrollable Content */}
              <div className="overflow-y-auto p-4 sm:p-6 md:p-7">
                {/* Status */}
                <div className="mb-6 flex items-center justify-between border-b pb-4">
                  <span className="text-sm font-semibold text-gray-500 sm:text-base">
                    Venue Status
                  </span>

                  <Badge
                    className={
                      selectedVenue.is_active
                        ? "bg-green-100 px-3 py-1 text-green-700 hover:bg-green-100"
                        : "bg-red-100 px-3 py-1 text-red-700 hover:bg-red-100"
                    }
                  >
                    {selectedVenue.is_active ? "Active" : "Inactive"}
                  </Badge>
                </div>

                {/* Description */}
                <div className="mb-6">
                  <h3 className="mb-2 text-lg font-semibold text-gray-900">
                    About Venue
                  </h3>

                  {selectedVenue.description ? (
                    <div
                      className="prose prose-sm max-w-none text-gray-600"
                      dangerouslySetInnerHTML={{
                        __html: selectedVenue.description,
                      }}
                    />
                  ) : (
                    <p className="text-sm text-gray-500">
                      No description available for this venue.
                    </p>
                  )}
                </div>

                {/* Location */}
                <div className="mb-6">
                  <h3 className="mb-3 text-lg font-semibold text-gray-900">
                    Location
                  </h3>

                  <div className="flex items-start gap-2 text-sm text-gray-600 sm:text-base">
                    <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-orange-600" />

                    <span>{selectedVenue.location}</span>
                  </div>
                </div>

                {/* Sports */}
                <div className="mb-6">
                  <h3 className="mb-3 text-lg font-semibold text-gray-900">
                    Sports Available
                  </h3>

                  <div className="flex flex-wrap gap-2">
                    {selectedVenue.sports?.length > 0 ? (
                      selectedVenue.sports.map((sport, index) => (
                        <Badge
                          key={index}
                          variant="secondary"
                          className="bg-orange-100 px-3 py-1.5 text-orange-700 hover:bg-orange-200"
                        >
                          <Flame className="mr-1 h-3.5 w-3.5" />
                          {sport}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-sm text-gray-500">
                        No sports information available.
                      </span>
                    )}
                  </div>
                </div>

                {/* Google Maps */}
                {selectedVenue.link && (
                  <a
                    href={selectedVenue.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="
              inline-flex
              w-full
              items-center
              justify-center
              gap-2
              rounded-lg
              bg-orange-600
              px-5
              py-3
              text-sm
              font-semibold
              text-white
              transition
              hover:bg-orange-700
              sm:w-auto
            "
                  >
                    <MapPin className="h-4 w-4" />
                    View on Google Maps
                    <ExternalLink className="h-4 w-4" />
                  </a>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
